import { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// import { element } from 'prop-types';
import SuperAdminLayout from '../layouts/super-admin/dashboard';
import AuthGuard from '../guards/AuthGuard';
import SuperAdminGuard from '../guards/SuperAdminGuard';
import { SUPER_ADMIN_DASHBOARD } from '../config';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();

    return (
        <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/super-admin/dashboard')} />}>
            <Component {...props} />
        </Suspense>
    );
};

const Dashboard = Loadable(lazy(() => import('../pages/users/super-admin/dashboard/Dashboard')));
const Roles = Loadable(lazy(() => import('../pages/user-management/Role/Roles')));
const RolePermission = Loadable(lazy(() => import('../pages/user-management/Role/RolePermission')));
const Shops = Loadable(lazy(() => import('../pages/shop-management/shop/Shops')));
const ShopOperation = Loadable(lazy(() => import('../pages/shop-management/shop/ShopOperation')));
const RoleOperation = Loadable(lazy(() => import('../pages/user-management/Role/RoleOperation')));
const Users = Loadable(lazy(() => import('../pages/users/super-admin/User/Users')));
const UserOperation = Loadable(lazy(() => import('../pages/users/super-admin/User/UserOperation')));
const UserAccount = Loadable(lazy(() => import('../pages/profile/UserAccount')));
const Languages = Loadable(lazy(() => import('../pages/users/super-admin/masters/Language/Languages')));
const LanguageOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Language/LanguageOperation')));
const Faqs = Loadable(lazy(() => import('../pages/users/super-admin/masters/FAQ/FAQs')));
const FaqOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/FAQ/FAQOperation')));
const Clients = Loadable(lazy(() => import('../pages/users/super-admin/masters/Client/Clients')));
const ClientOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Client/ClientOperation')));
// Removed SuperAdmin QR Logs views; Client will manage QR Logs
const NotificationTypeOperation = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/NotificationType/NotificationTypeOperation'))
);
const NotificationTypes = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/NotificationType/NotificationTypes'))
);
// const Categories = Loadable(lazy(() => import('../pages/users/super-admin/masters/Category/Categories')));
// const CategoryOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Category/CategoryOperation')));
const Videos = Loadable(lazy(() => import('../pages/users/super-admin/masters/Video/Videos')));
const VideoOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Video/VideoOperation')));
const PageContent = Loadable(lazy(() => import('../pages/users/super-admin/masters/PageContent/PageContents')));
const PageContentOperation = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/PageContent/PageContentOperation'))
);
const GlobalSettings = Loadable(lazy(() => import('../pages/users/super-admin/masters/GlobalSettings/GlobalSettings')));
const GlobalSettingsOperation = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/GlobalSettings/GlobalSettingsOperation'))
);
const SocialLinks = Loadable(lazy(() => import('../pages/users/super-admin/masters/SocialLink/Social_links')));
const SocialLinkOperations = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/SocialLink/social_linkOperation'))
);
const Cities = Loadable(lazy(() => import('../pages/users/super-admin/masters/City/Cities')));
const CityOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/City/CityOperation')));
const States = Loadable(lazy(() => import('../pages/users/super-admin/masters/State/States')));
const StateOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/State/StateOperation')));
const Countries = Loadable(lazy(() => import('../pages/users/super-admin/masters/Country/Countries')));
const CountryOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Country/CountryOperation')));
const QRForm = Loadable(lazy(() => import('../pages/users/super-admin/masters/QR/QRForm')));
const SysProducts = Loadable(lazy(() => import('../pages/users/super-admin/masters/Product/Checklists/Packages')));
const SysProductOperation = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/Product/Checklists/PackageOperation'))
);
const SysProductsTimeRanges = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/Product/ChecklistItems/Features'))
);
const SysProductTimeRangeOperation = Loadable(
    lazy(() => import('../pages/users/super-admin/masters/Product/ChecklistItems/FeatureOperation'))
);

const DelayModule = Loadable(lazy(() => import('../pages/users/super-admin/masters/DelayModule/DelayModules')));
const DelayModuleOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/DelayModule/DelayModuleOperation')));
const DelayModuleTimingPage = Loadable(lazy(() => import('../pages/users/super-admin/masters/DelayModule/DelayModuleTimingPage')));
const QrLogActions = Loadable(lazy(() => import('../pages/users/super-admin/masters/QRLogActions/QRLogActions')));
const QRLogActionOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/QRLogActions/QRLogActionOperation')));
const Staff = Loadable(lazy(() => import('../pages/users/super-admin/masters/Staff/Staff')));
const StaffOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/Staff/StaffOperation')));
const UserTrack = Loadable(lazy(() => import('../pages/users/super-admin/masters/UserTrack/UserTrack')));
const ActionCodes = Loadable(lazy(() => import('../pages/users/super-admin/masters/ActionCode/ActionCodes')));
const ActionCodeOperation = Loadable(lazy(() => import('../pages/users/super-admin/masters/ActionCode/ActionCodeOperation')));
const InactiveUsers = Loadable(lazy(() => import('../pages/users/super-admin/masters/InactiveUsers/InactiveUsers')));
const PackagingList = Loadable(lazy(() => import('../pages/users/super-admin/masters/Packaging/PackagingList')));

const SuperAdminRoutes = {
    path: '/super-admin',
    element: (
        <AuthGuard>
            <SuperAdminGuard>
                <SuperAdminLayout />
            </SuperAdminGuard>
        </AuthGuard>
    ),
    children: [
        { element: <Navigate to={SUPER_ADMIN_DASHBOARD} replace />, index: true },
        { path: 'dashboard', element: <Dashboard /> },
        {
            path: 'profile',
            children: [{ path: 'edit-profile', element: <UserAccount /> }],
        },
        {
            path: 'user-management',
            children: [
                { path: 'roles', element: <Roles /> },
                { path: 'role/:id/permission', element: <RolePermission /> },
                { path: 'role/add', element: <RoleOperation /> },
                { path: 'role/:id/edit', element: <RoleOperation /> },
                { path: 'role/:id/view', element: <RoleOperation /> },
                { path: 'users', element: <Users /> },
                { path: 'user/add', element: <UserOperation /> },
                { path: 'user/:id/edit', element: <UserOperation /> },
                { path: 'user/:id/view', element: <UserOperation /> },
                // { path: 'user-approval', element: <UserApproval /> },
                // { path: 'user-approval/:id/edit', element: <UserApprovalOperation /> },
            ],
        },
        {
            path: 'shop-management',
            children: [
                { path: 'shops', element: <Shops /> },
                { path: 'shops/add', element: <ShopOperation /> },
                { path: 'shops/:id/edit', element: <ShopOperation /> },
                { path: 'shops/:id/view', element: <ShopOperation /> },
            ],
        },
        {
            path: 'master',
            children: [
                { path: 'languages', element: <Languages /> },
                { path: 'language/add', element: <LanguageOperation /> },
                { path: 'language/:id/edit', element: <LanguageOperation /> },
                { path: 'language/:id/view', element: <LanguageOperation /> },
                { path: 'faqs', element: <Faqs /> },
                { path: 'faq/add', element: <FaqOperation /> },
                { path: 'faq/:id/edit', element: <FaqOperation /> },
                { path: 'faq/:id/view', element: <FaqOperation /> },
                { path: 'clients', element: <Clients /> },
                { path: 'client/add', element: <ClientOperation /> },
                { path: 'client/:id/edit', element: <ClientOperation /> },
                { path: 'client/:id/view', element: <ClientOperation /> },
                { path: 'videos', element: <Videos /> },
                { path: 'videos/add', element: <VideoOperation /> },
                { path: 'videos/:id/edit', element: <VideoOperation /> },
                { path: 'videos/:id/view', element: <VideoOperation /> },
                { path: 'notification-types', element: <NotificationTypes /> },
                { path: 'notification-types/add', element: <NotificationTypeOperation /> },
                { path: 'notification-types/:id/edit', element: <NotificationTypeOperation /> },
                { path: 'notification-types/:id/view', element: <NotificationTypeOperation /> },
                { path: 'social_links', element: <SocialLinks /> },
                { path: 'social_links/add', element: <SocialLinkOperations /> },
                { path: 'social_links/:id/edit', element: <SocialLinkOperations /> },
                { path: 'social_links/:id/view', element: <SocialLinkOperations /> },
                { path: 'countries', element: <Countries /> },
                { path: 'countries/add', element: <CountryOperation /> },
                { path: 'countries/:id/edit', element: <CountryOperation /> },
                { path: 'countries/:id/view', element: <CountryOperation /> },
                { path: 'states', element: <States /> },
                { path: 'states/add', element: <StateOperation /> },
                { path: 'states/:id/edit', element: <StateOperation /> },
                { path: 'states/:id/view', element: <StateOperation /> },
                { path: 'cities', element: <Cities /> },
                { path: 'cities/add', element: <CityOperation /> },
                { path: 'cities/:id/edit', element: <CityOperation /> },
                { path: 'cities/:id/view', element: <CityOperation /> },
                {
                    path: 'delay-modules',
                    children: [
                        { path: '', element: <DelayModule /> },
                        { path: 'add', element: <DelayModuleOperation /> },
                        { path: ':delayModuleId/edit', element: <DelayModuleOperation /> },
                        { path: ':delayModuleId/view', element: <DelayModuleOperation /> },
                        { path: ':delayModuleId/timings', element: <DelayModuleTimingPage />}
                    ],
                },
                { path: 'qr/add', element: <QRForm /> },
                { path: 'products', element: <SysProducts /> },
                { path: 'products/add', element: <SysProductOperation /> },
                { path: 'products/:id/edit', element: <SysProductOperation /> },
                { path: 'products/:id/view', element: <SysProductOperation /> },
                { path: 'products/:product_id/time-ranges', element: <SysProductsTimeRanges /> },
                { path: 'products/:product_id/time-ranges/add', element: <SysProductTimeRangeOperation /> },
                { path: 'products/:product_id/time-ranges/:id/edit', element: <SysProductTimeRangeOperation /> },
                { path: 'products/:product_id/time-ranges/:id/view', element: <SysProductTimeRangeOperation /> },
                { path: 'qr-log-actions', element: <QrLogActions /> },
                { path: 'qr-log-actions/:id/view', element: <QRLogActionOperation /> },
                { path: 'staff', element: <Staff /> },
                { path: 'staff/add', element: <StaffOperation /> },
                { path: 'staff/:id/edit', element: <StaffOperation /> },
                { path: 'staff/:id/view', element: <StaffOperation /> },
                { path: 'user-track', element: <UserTrack /> },
                { path: 'inactive-users', element: <InactiveUsers /> },
                { path: 'action-code', element: <Navigate to="/super-admin/master/action-codes" replace /> },
                { path: 'action-codes', element: <ActionCodes /> },
                { path: 'action-code/add', element: <ActionCodeOperation /> },
                { path: 'action-code/:id/edit', element: <ActionCodeOperation /> },
                { path: 'action-code/:id/view', element: <ActionCodeOperation /> },
                { path: 'packaging', element: <PackagingList /> },
            ],
        },

        {
            path: 'page-content',
            children: [
                { path: '', element: <PageContent /> },
                { path: 'add', element: <PageContentOperation /> },
                { path: ':id/edit', element: <PageContentOperation /> },
                { path: ':id/view', element: <PageContentOperation /> },
            ],
        },
        {
            path: 'global-settings',
            children: [
                { path: '', element: <GlobalSettings /> },
                { path: 'add', element: <GlobalSettingsOperation /> },
                { path: ':id/edit', element: <GlobalSettingsOperation /> },
                { path: ':id/view', element: <GlobalSettingsOperation /> },
            ],
        },
    ],
};

export default SuperAdminRoutes;
